import { supabase } from '../lib/supabase';

// Guest progress lives in localStorage so that a refresh — or the gap between
// swiping and deciding to create an account — doesn't throw the work away.
//
// Shape on disk: { [deckId]: { known: number[], learning: number[] } }
// Shape in app:  { [deckId]: { known: Set,      learning: Set      } }

const STORAGE_KEY = 'ripety_guest_progress';
const PROMPT_KEY = 'ripety_save_prompt_dismissed';

export const loadGuestProgress = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const progress = {};

    Object.entries(parsed).forEach(([deckId, sets]) => {
      progress[deckId] = {
        known: new Set(Array.isArray(sets?.known) ? sets.known : []),
        learning: new Set(Array.isArray(sets?.learning) ? sets.learning : []),
      };
    });

    return progress;
  } catch (err) {
    // Corrupt or unreadable (private mode, cleared storage) — start clean
    // rather than trapping the user on a broken deck.
    console.warn('Could not read guest progress:', err);
    return null;
  }
};

export const saveGuestProgress = (deckProgress) => {
  try {
    const serialisable = {};

    Object.entries(deckProgress).forEach(([deckId, sets]) => {
      const known = [...(sets?.known || [])];
      const learning = [...(sets?.learning || [])];
      if (known.length || learning.length) {
        serialisable[deckId] = { known, learning };
      }
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(serialisable));
  } catch (err) {
    // Quota or private mode — progress just won't survive the refresh.
    console.warn('Could not save guest progress:', err);
  }
};

export const clearGuestProgress = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn('Could not clear guest progress:', err);
  }
};

export const countGuestCards = (deckProgress) => {
  if (!deckProgress) return 0;
  return Object.values(deckProgress).reduce(
    (total, sets) => total + (sets?.known?.size || 0) + (sets?.learning?.size || 0),
    0
  );
};

export const isSavePromptDismissed = () => {
  try {
    return localStorage.getItem(PROMPT_KEY) === 'true';
  } catch {
    return false;
  }
};

export const dismissSavePrompt = () => {
  try {
    localStorage.setItem(PROMPT_KEY, 'true');
  } catch (err) {
    console.warn('Could not record prompt dismissal:', err);
  }
};

/**
 * Move whatever the guest built up into their new account.
 *
 * Called once, right after authentication. Custom cards are excluded: guests
 * can't create them, so every index here is a real deck position.
 *
 * Returns the number of rows written (0 if there was nothing to move).
 */
export const migrateGuestProgressToAccount = async (userId) => {
  const guest = loadGuestProgress();
  if (!guest) return 0;

  const rows = [];
  const now = new Date().toISOString();

  Object.entries(guest).forEach(([deckId, sets]) => {
    sets.known.forEach(index => {
      rows.push({ user_id: userId, deck_id: deckId, character_index: index, known: true, last_seen: now });
    });
    sets.learning.forEach(index => {
      rows.push({ user_id: userId, deck_id: deckId, character_index: index, known: false, last_seen: now });
    });
  });

  if (rows.length === 0) {
    clearGuestProgress();
    return 0;
  }

  try {
    const { error } = await supabase
      .from('user_progress')
      .upsert(rows, { onConflict: 'user_id,deck_id,character_index' });

    if (error) {
      // Leave localStorage intact so the next sign-in can retry rather than
      // silently dropping the work.
      console.error('Could not migrate guest progress:', error.code, error.message);
      return 0;
    }

    clearGuestProgress();
    return rows.length;
  } catch (err) {
    console.error('Could not migrate guest progress:', err);
    return 0;
  }
};
